import React from 'react';
import { Box, Drawer, Skeleton, Typography } from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckIcon from '@mui/icons-material/Check';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { getNativeDrawerPortalProps } from '../../../utils/mobileView';
import {
  getMobileHotelPickerBackdropSx,
  getMobileHotelPickerCheckSx,
  getMobileHotelPickerEmptySx,
  getMobileHotelPickerHandleSx,
  getMobileHotelPickerHeaderSx,
  getMobileHotelPickerIconSx,
  getMobileHotelPickerItemSx,
  getMobileHotelPickerListSx,
  getMobileHotelPickerMetaSx,
  getMobileHotelPickerNameSx,
  getMobileHotelPickerSheetPaperSx,
  getMobileHotelPickerSubtitleSx,
  getMobileHotelPickerTitleSx,
} from '../../../utils/mobileHotelPickerLayout';

function formatHotelLocation(hotel) {
  if (hotel.city && hotel.country) return `${hotel.city}, ${hotel.country}`;
  return hotel.city || hotel.country || 'Location not specified';
}

function MobileHotelPickerDataSkeleton() {
  return (
    <Box
      sx={{
        ...getMobileHotelPickerItemSx(false),
        cursor: 'default',
        pointerEvents: 'none',
      }}
    >
      <Box sx={getMobileHotelPickerIconSx(false)}>
        <HotelIcon sx={{ fontSize: 22 }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Skeleton variant="text" width="68%" height={24} />
        <Skeleton variant="text" width="44%" height={20} sx={{ mt: 0.25 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mt: 0.35 }}>
          <StarRoundedIcon sx={{ fontSize: 13, color: '#f8e082' }} />
          <Skeleton variant="text" width={54} height={18} />
        </Box>
      </Box>

      <Skeleton variant="circular" width={34} height={34} sx={{ flexShrink: 0 }} />
    </Box>
  );
}

export default function MobileHotelPickerSheet({
  open,
  onClose,
  hotels = [],
  selectedHotel,
  loadingHotels = false,
  onSelectHotel,
}) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      {...getNativeDrawerPortalProps()}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: getMobileHotelPickerSheetPaperSx(),
      }}
      slotProps={{
        backdrop: {
          sx: getMobileHotelPickerBackdropSx(),
        },
      }}
      sx={{ zIndex: 1600 }}
    >
      <Box sx={getMobileHotelPickerHandleSx()} />

      <Box sx={getMobileHotelPickerHeaderSx()}>
        <Typography sx={getMobileHotelPickerTitleSx()}>Select Hotel</Typography>
        <Typography sx={getMobileHotelPickerSubtitleSx()}>
          Choose a hotel to switch context
        </Typography>
      </Box>

      <Box sx={getMobileHotelPickerListSx()}>
        {loadingHotels ? (
          <MobileHotelPickerDataSkeleton />
        ) : hotels.length === 0 ? (
          <Typography sx={getMobileHotelPickerEmptySx()}>No hotels available</Typography>
        ) : (
          hotels.map((hotel) => {
            const isSelected = selectedHotel?.id === hotel.id;
            return (
              <Box
                key={hotel.id}
                component="button"
                type="button"
                onClick={() => onSelectHotel?.(hotel.id)}
                sx={{
                  ...getMobileHotelPickerItemSx(isSelected),
                  appearance: 'none',
                  WebkitAppearance: 'none',
                }}
              >
                <Box sx={getMobileHotelPickerIconSx(isSelected)}>
                  <HotelIcon sx={{ fontSize: 22 }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={getMobileHotelPickerNameSx(isSelected)}>{hotel.name}</Typography>
                  <Typography sx={getMobileHotelPickerMetaSx()}>{formatHotelLocation(hotel)}</Typography>
                  {hotel.star_rating_display ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mt: 0.35 }}>
                      <StarRoundedIcon sx={{ fontSize: 13, color: '#f8e082' }} />
                      <Typography sx={{ ...getMobileHotelPickerMetaSx(), mt: 0 }}>
                        {hotel.star_rating_display}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>

                {isSelected ? (
                  <Box sx={getMobileHotelPickerCheckSx()}>
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </Box>
                ) : null}
              </Box>
            );
          })
        )}
      </Box>
    </Drawer>
  );
}
